// disOS session state. One source of truth for "who is signed in", loaded once on app start.
import { create } from 'zustand';
import { authApi, settingsApi, type AuthUser, type FirmSettings } from '../api/client';

export type AuthStatus = 'loading' | 'authed' | 'anon';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** Per-user report settings (firm footer). Empty when signed out. */
  firm: FirmSettings;
  /** Resolve the current session from the cookie (called once at startup). */
  load(): Promise<void>;
  login(email: string, password: string): Promise<void>;
  signup(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /** Save the firm footer settings for the signed-in user. */
  saveFirm(firm: FirmSettings): Promise<void>;
  /** Change the signed-in account's email (login identity). */
  updateEmail(email: string): Promise<void>;
}

async function loadFirm(): Promise<FirmSettings> {
  try { return await settingsApi.get(); } catch { return {}; }
}

export const useAuth = create<AuthState>((set) => ({
  status: 'loading',
  user: null,
  firm: {},
  async load() {
    try {
      const user = await authApi.me();
      if (user) set({ status: 'authed', user, firm: await loadFirm() });
      else set({ status: 'anon', user: null, firm: {} });
    } catch {
      set({ status: 'anon', user: null, firm: {} });
    }
  },
  async login(email, password) {
    const user = await authApi.login(email, password);
    set({ status: 'authed', user, firm: await loadFirm() });
  },
  async signup(email, password) {
    const user = await authApi.signup(email, password);
    set({ status: 'authed', user, firm: await loadFirm() });
  },
  async logout() {
    try { await authApi.logout(); } finally { set({ status: 'anon', user: null, firm: {} }); }
  },
  async saveFirm(firm) {
    const saved = await settingsApi.update(firm);
    set({ firm: saved });
  },
  async updateEmail(email) {
    const user = await authApi.updateEmail(email);
    set({ user });
  },
}));
